import * as Icons from "@mui/icons-material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChecklistIcon from "@mui/icons-material/Checklist";
import LocalShipping from "@mui/icons-material/LocalShipping";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import TerminalIcon from "@mui/icons-material/Terminal";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import AvatarWithStatus from "@/components/AvatarWithStatus.tsx";
import FirstLocationRedirectHook from "@/components/FirstLocationRedirectHook.tsx";
import ChannelDial from "@/components/Icons/ChannelDial.tsx";
import ManageUsers from "@/components/Icons/ManageUsers";
import TemplateIcon from "@/components/Icons/TemplateIcon.tsx";
import LanguageDropButton from "@/components/LanguageDropButton.tsx";
import PortalDialog from "@/components/PortalDialog.tsx";
import ReadOnlyModeToggle from "@/components/ReadOnlyModeToggle.tsx";
import ToggleSecondWindowButton from "@/components/SecondWindow/ToggleSecondWindowButton.tsx";
import SqlEditor from "@/components/SqlEditor.tsx";
import UserFeedbackForm from "@/components/UserFeedbackForm.tsx";
import VerticalLinks from "@/components/VerticalLinks.tsx";
import UseFirebase from "@/hooks/firebase/useFirebase";
import { useGlobalFlags } from "@/hooks/useGlobalFlags.ts";
import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";
import { useTeamAliases } from "@/hooks/useTeamAliases";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { channelUnreadCount, driverUnreadCount } from "@/models/repositories";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";
import { TrackingContext } from "@/models/TrackingStateProvider.tsx";
import { UxContext } from "@/models/UxStateProvider.tsx";
import { isElectron } from "@/utils";

export type LeftSidebarType = {
  label: string;
  id: string;
  url?: string;
  /** When true, the control is shown but not navigable (e.g. admin-only without permission). */
  disabled?: boolean;
  icon?: React.ReactNode;
  component?: React.ReactNode;
  active?: boolean;
  hasUnreads?: boolean;
  onClick?: () => void;
  bgSx?: Record<string, unknown>;
};

export default function LeftSidebar() {
  const { setGlobalSearchOpen } = useContext(UxContext);
  const { isWorkspaceAdmin, isWorkspaceLimitedMember, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { myAccount } = useContext(MyAccountContext);
  const { ampli } = useContext(TrackingContext);
  const { workspaceId, subPage } = useParams();
  const workspaceAdmin = isWorkspaceAdmin();
  const limitedMember = isWorkspaceLimitedMember();
  const { readOnlyMode, sqlEditor } = useGlobalFlags();
  const [readOnlyActive, setReadOnlyActive] = useState<boolean>(false);
  const [sqlEditorOpen, setSqlEditorOpen] = useState<boolean>(false);
  const [open, setOpen] = useState<{
    title?: string;
    content: React.ReactNode;
  } | null>(null);
  const { notificationsState, requestDesktopNotifications } = UseFirebase();
  const [deniedModalOpen, setDeniedModalOpen] = useState(false);

  const { redirectUrl, list: channelListForNav } = FirstLocationRedirectHook();
  const driversLink = redirectUrl("drivers");
  const channelsLink = redirectUrl("channels");

  // Persist per workspace so the icon stays put across route changes that remount the sidebar
  // (e.g. drivers -> channels -> drivers). Without this cache, the channels icon flashes back in
  // each time because the reactive query takes a tick to repopulate after remount.
  const channelsNavItemCacheKey = workspaceId ? `showChannelsNavItem:${workspaceId}` : "showChannelsNavItem:none";
  const [showChannelsNavItem, setShowChannelsNavItem] = useSafeLocalStorage<boolean>(channelsNavItemCacheKey, false);

  useEffect(() => {
    if (channelListForNav == null || workspaceId == null) return;
    const inWorkspace = channelListForNav.filter((c) => c.workspaceId === workspaceId);
    if (inWorkspace.length === 0 && channelListForNav.length > 0) {
      return;
    }
    setShowChannelsNavItem(
      inWorkspace.some((c) => !c.aliasChannel && c.id && c.workspaceId && c.enabled && c.name === "read"),
    );
  }, [channelListForNav, workspaceId, setShowChannelsNavItem]);

  const pathName = window.location.pathname;
  const workspaceMembershipId = myCurrentWorkspaceMembership?.id;
  const { teamAliases } = useTeamAliases(workspaceId ?? "", workspaceMembershipId ?? "");

  const driverUnreads =
    useReactiveSelectQuery(
      driverUnreadCount,
      [{ workspaceId, workspaceMembershipId, teamAliases: teamAliases.length > 0 ? teamAliases : undefined }],
      "LeftSidebar:driverUnreadCount",
    ) ?? [];

  const channelUnreads =
    useReactiveSelectQuery(
      channelUnreadCount,
      [{ workspaceId, workspaceMembershipId }],
      "LeftSidebar:channelUnreadCount",
    ) ?? [];

  const unreads = useMemo(() => {
    return {
      drivers: (driverUnreads[0]?.count ?? 0) > 0,
      channels: (channelUnreads[0]?.count ?? 0) > 0,
    };
  }, [driverUnreads, channelUnreads]);

  const channelsItem: LeftSidebarType = {
    label: "Channels",
    url: channelsLink ?? `/workspaces/${workspaceId}/channels`,
    id: "channels",
    icon: <ChannelDial />,
    active: pathName.includes("/channels/") || subPage === "channels",
    hasUnreads: unreads.channels,
    onClick: () => ampli.driverTable(),
  };

  const topLinks: LeftSidebarType[] = [
    {
      label: "Drivers",
      url: driversLink ?? `/workspaces/${workspaceId}/drivers`,
      id: "drivers",
      icon: <LocalShipping />,
      active: pathName.includes("/drivers/") || subPage === "drivers",
      hasUnreads: unreads.drivers,
      onClick: () => ampli.driverTable(),
    },
    ...(showChannelsNavItem ? [channelsItem] : []),
    {
      label: "Tasks",
      url: `/workspaces/${workspaceId}/tasks`,
      id: "tasks",
      icon: <ChecklistIcon />,
      active: subPage === "tasks",
    },
    {
      label: "Live Feed",
      url: `/workspaces/${workspaceId}/live-feed`,
      id: "live-feed",
      icon: <RssFeedIcon />,
      active: subPage === "live-feed",
    },
    {
      icon: <CalendarMonthIcon />,
      label: "Scheduled Messages",
      url: `/workspaces/${workspaceId}/scheduled-messages`,
      id: "scheduled-messages",
      onClick: () => ampli.scheduledMessages(),
    },
    ...(!limitedMember
      ? [
          {
            label: "Templates",
            url: `/workspaces/${workspaceId}/manage-templates`,
            id: "manage-templates",
            icon: <TemplateIcon />,
            onClick: () => ampli.templates(),
          },
        ]
      : []),
    ...(workspaceAdmin
      ? [
          {
            label: "Manage Users",
            url: `/workspaces/${workspaceId}/manage-users`,
            icon: <ManageUsers />,
            id: "manage-users",
            onClick: () => ampli.manageUsers(),
          },
        ]
      : []),
  ]
    .filter((link): link is NonNullable<typeof link> => !!link)
    .filter((link) => link.url)
    .map((link) => ({
      ...link,
      active: link?.active || (subPage && subPage === link.id),
    }));

  const readOnlyModeButton = readOnlyMode
    ? {
        label: "Read Only Mode",
        component: <ReadOnlyModeToggle type="button" setActive={(value) => setReadOnlyActive(value)} />,
        id: "read-only-mode",
        active: readOnlyActive,
      }
    : null;

  const sqlWindowButton = sqlEditor
    ? {
        label: "SQL Editor",
        icon: <TerminalIcon />,
        id: "sql-editor",
        onClick: () => setSqlEditorOpen(() => true),
      }
    : null;

  const handleNotificationClick = async () => {
    if (notificationsState === "denied") {
      setDeniedModalOpen(true);
      return;
    }
    await requestDesktopNotifications?.();
  };

  const notificationButton: LeftSidebarType | null =
    !isElectron && notificationsState !== "granted"
      ? {
          label: notificationsState === "denied" ? "Notifications Blocked" : "Enable Notifications",
          icon: notificationsState === "denied" ? <NotificationsOffIcon /> : <NotificationsIcon />,
          id: "enable-notifications",
          onClick: handleNotificationClick,
          bgSx:
            notificationsState === "denied"
              ? { bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }
              : { bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } },
        }
      : null;

  const bottomLinks: LeftSidebarType[] = [
    notificationButton,
    {
      label: "Search Workspace",
      icon: <Icons.Search role="img" />,
      id: "search-workspace",
      onClick: () => {
        ampli.searchClick({ workspaceId });
        setGlobalSearchOpen(true);
      },
    },
    {
      label: "Activate Second Window",
      component: <ToggleSecondWindowButton />,
      id: "activate-second-window",
    },
    {
      label: "Language",
      component: <LanguageDropButton />,
      id: "language",
      onClick: () => ampli.languageModal(),
    },
    {
      label: "Need Help?",
      icon: <Icons.Help role="presentation" />,
      onClick: () =>
        setOpen(() => ({
          title: "Leave Feedback",
          content: (
            <UserFeedbackForm
              onClose={() => {
                setOpen(null);
              }}
            />
          ),
        })),
      id: "need-help",
    },
    readOnlyModeButton,
    sqlWindowButton,
    {
      label: "Your Profile",
      icon: <AvatarWithStatus accountId={myAccount?.id} />,
      id: "your-profile",
      url: `/workspaces/${workspaceId}/your-profile`,
      active: subPage === "your-profile",
      onClick: () => ampli.userSettings(),
    },
  ].filter((link): link is NonNullable<typeof link> => !!link);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          bgcolor: "rgba(0,0,0,0.5)",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          borderRight: "1px solid var(--color-zinc-700)",
        }}
      >
        <Box>
          <VerticalLinks links={topLinks} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>&nbsp;</Box>
        <Box>
          <VerticalLinks links={bottomLinks} />
        </Box>
      </Box>

      <SqlEditor open={sqlEditorOpen} close={() => setSqlEditorOpen(() => false)} />
      <PortalDialog title={open?.title} open={open !== null} onClose={() => setOpen(null)}>
        {open?.content}
      </PortalDialog>
      <PortalDialog title="Notifications Blocked" open={deniedModalOpen} onClose={() => setDeniedModalOpen(false)}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ color: "var(--color-zinc-300)" }}>Browser notifications have been blocked. To re-enable them:</Box>
          <Box
            component="ol"
            sx={{
              py: 0,
              px: 1,
              listStyleType: "decimal",
              listStylePosition: "inside",
              color: "var(--color-zinc-300)",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <li>Click the lock or site-settings icon in your browser's address bar</li>
            <li>
              Find the <strong>Notifications</strong> permission
            </li>
            <li>
              Change it from <strong>Block</strong> to <strong>Allow</strong>
            </li>
            <li>Reload this page</li>
          </Box>
          <Button
            onClick={() => setDeniedModalOpen(false)}
            sx={{
              alignSelf: "flex-end",
              bgcolor: "#2563eb",
              "&:hover": { bgcolor: "#1d4ed8" },
              color: "#fff",
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              textTransform: "none",
              width: "auto",
            }}
          >
            Got It
          </Button>
        </Box>
      </PortalDialog>
    </>
  );
}
